import { useEffect, useState, useMemo } from 'react'
import Grid from '@mui/material/Grid2';
import { Avatar, Box, Button, Typography, Tooltip } from '@mui/material';
import { MsgIcon, OfficeIcon, PhoneIcon, SalesIcon } from '../assets/icons';
import { decodeIdToken, decodeMetadataToken } from '../utils/token.utils';
import { STORAGE_KEYS } from '../utils/storage.util';
import { toast } from 'react-toastify';
import AuthService from '../services/auth.service';
import ChargebeeService from '../services/chargebee.service';
import { config } from '../config';
import SubscriptionAlert from './SubscriptionAlert';
import PaymentWarning from './PaymentWarning';
import Loader from './common/Loader';
import SalesBannerCountdown from './common/SaleBannerCountdown';
import { PaymentMethodStatusData } from '../types/chargebee';
import { formatDate, checkFeatureEntitlement } from '../utils/common.util';
import { getAilaunchRedirectUrl, shouldUseAilaunchLaunchForPlan } from '../utils/redirect.util';
import MixpanelService from '../services/mixpanel.service';

const Overview = ({ navigate }: any) => {
    const [userDetails, setUserDetails] = useState({
        name: '',
        email: '',
        phone: '',
        username: '',
        picture: '',
        company: ''
    });

    const [isLaunchingEasyWebinar, setIsLaunchingEasyWebinar] = useState(false);
    const [showSubscriptionCard, setShowSubscriptionCard] = useState(true);
    const [showWorkspacesCard, setShowWorkspacesCard] = useState(true);
    const [showSubscriptionAlert, setShowSubscriptionAlert] = useState(false);
    const [isEasyCRMEnabled, setIsEasyCRMEnabled] = useState(true);
    const [paymentStatus, setPaymentStatus] = useState<PaymentMethodStatusData | null>(null);
    const [showPaymentWarning, setShowPaymentWarning] = useState(false);
    const [subscriptionDetails, setSubscriptionDetails] = useState<any>(null);
    const [isLoadingSubscriptionDetails, setIsLoadingSubscriptionDetails] = useState(false);

    // Helper function to determine if sale should be shown based on subscription conditions
    const shouldShowSale = useMemo(() => {
        const metadata = decodeMetadataToken();
        // Don't show sale banners to users with parentCompanyId
        if (metadata?.companies?.[0]?.parentCompanyId) {
            return false;
        }
        return config.isSaleLive && (
            metadata?.companies?.[0]?.subscription?.status === "in_trial" ||
            metadata?.companies?.[0]?.subscription?.planId?.includes("free-trial-plan") ||
            metadata?.companies?.[0]?.subscription?.planId?.includes("USD-Monthly")
        );
    }, []);

    // Helper function to get status color and display text
    const getStatusInfo = (status: string) => {
        switch (status) {
            case 'in_trial':
                return { color: '#FFA500', text: 'Trial' };
            case 'active':
                return { color: '#4CAF50', text: 'Active' };
            case 'cancelled':
                return { color: '#F44336', text: 'Cancelled' };
            case 'paused':
                return { color: '#FF9800', text: 'Paused' };
            case 'non_renewing':
                return { color: '#F44336', text: 'Non-renewing' };
            default:
                return { color: '#9E9E9E', text: status || 'Unknown' };
        }
    };

    // Helper component for skeleton loading
    const SkeletonBox = ({ width, height = '16px' }: { width: string; height?: string }) => (
        <Box 
            sx={{ 
                height, 
                width, 
                backgroundColor: '#E0E0E0', 
                borderRadius: '4px',
                animation: 'pulse 1.5s ease-in-out infinite',
                '@keyframes pulse': {
                    '0%': { opacity: 0.6 },
                    '50%': { opacity: 0.3 },
                    '100%': { opacity: 0.6 }
                }
            }} 
        />
    );

    useEffect(() => {
        const idTokenDetails = () => {
            try {
                const payload = decodeIdToken();
                const metadata = decodeMetadataToken();
                const userEmail = payload?.email || '';
                
                setUserDetails({
                    name: payload?.name || '--- ---',
                    email: userEmail,
                    phone: payload?.phone_number || '--- ---',
                    username: payload?.preferred_username || '--- ---',
                    picture: payload?.picture || '',
                    company: metadata?.companies?.[0]?.name || '--- ---'
                });
                
                // Check CRM entitlement
                const crmEntitlement = checkFeatureEntitlement('easycrm');
                setIsEasyCRMEnabled(crmEntitlement.isEnabled);

                // Check if company has parentCompanyId - hide subscription and workspaces cards if it does
                if (metadata?.companies && metadata.companies.length > 0) {
                    if (metadata.companies[0].parentCompanyId) {
                        setShowSubscriptionCard(false);
                        setShowWorkspacesCard(false);
                    }

                    const role = metadata.companies[0]?.applicationRoles['EASYWEBINAR']
                    if (role !== 'OWNER') {
                        setShowSubscriptionCard(false);
                    }
                }
            } catch (error) {
                console.error('Error decoding token:', error);
            }
        };

        idTokenDetails();
    }, []);

    // Fetch payment method status
    useEffect(() => {
        const fetchPaymentStatus = async () => {
            try {
                const metadata = decodeMetadataToken();
                const customerId = metadata?.companies?.[0]?.id;
                
                if (customerId) {
                    const response = await ChargebeeService.getPaymentMethodStatus(customerId);
                    setPaymentStatus(response.data);
                    
                    // Show warning if there are payment issues
                    const hasPaymentIssues = response.data.hasExpiredCard || 
                                           response.data.hasInvalidCard || 
                                           response.data.hasExpiringCard || 
                                           !response.data.hasPaymentMethod;
                    
                    setShowPaymentWarning(hasPaymentIssues);
                }
            } catch (error) {
                console.error('Error fetching payment status:', error);
                // Don't show error to user, just log it
            }
        };

        fetchPaymentStatus();
    }, []);

    // Fetch subscription details
    useEffect(() => {
        const fetchSubscriptionDetails = async () => {
            try {
                const metadata = decodeMetadataToken();
                const customerId = metadata?.companies?.[0]?.id;
                
                if (customerId && showSubscriptionCard) {
                    setIsLoadingSubscriptionDetails(true);
                    const response = await ChargebeeService.getSubscriptionDetails(customerId);
                    setSubscriptionDetails(response.data);
                }
            } catch (error) {
                console.error('Error fetching subscription details:', error);
                // Don't show error to user, just log it
            } finally {
                setIsLoadingSubscriptionDetails(false);
            }
        };

        fetchSubscriptionDetails();
    }, [showSubscriptionCard]);

    const handleEasyCRMClick = (e: React.MouseEvent) => {
        if (e.button === 0) {
            e.preventDefault();
            
            // Check entitlement before allowing access
            const crmEntitlement = checkFeatureEntitlement('easycrm');
            if (!crmEntitlement.isEnabled) {
                toast.error(crmEntitlement.message || 'Upgrade plan to access EasyCRM');
                return;
            }
            
            const state = localStorage.getItem(STORAGE_KEYS.STATE)
            const easyCRMUrl = `${config.easycrm_url}/user/MaintainSession${state ? `?state=${state}` : ''}`;
            window.location.href = easyCRMUrl;
        }
    }

    const handleEasyWebinarClick = async (e: React.MouseEvent) => {
        if (e.button === 0) {
            e.preventDefault();

            try {
                // Show loading indicator
                setIsLaunchingEasyWebinar(true);

                // First refresh the token to ensure we have the latest subscription data
                await AuthService.refreshToken();

                // Check subscription status in metadata token
                const metadata = decodeMetadataToken();
                const subscriptionStatus = metadata?.companies?.[0]?.subscription?.status;

                if (subscriptionStatus === 'cancelled') {
                    // Show error message if subscription is cancelled
                    toast.error('Your subscription has been cancelled. Please renew your subscription to access EasyWebinar.');
                    setIsLaunchingEasyWebinar(false);
                    return;
                }

                // If subscription is active, proceed with navigation
                const state = localStorage.getItem(STORAGE_KEYS.STATE);
                const tokenPlanId = metadata?.companies?.[0]?.subscription?.planId;
                const storedPlanId = localStorage.getItem('planId');
                const useAiLaunch =
                    shouldUseAilaunchLaunchForPlan(tokenPlanId) || shouldUseAilaunchLaunchForPlan(storedPlanId);
                const easyWebinarUrl = useAiLaunch
                    ? `${getAilaunchRedirectUrl()}&isLogin=true`
                    : `${config.easywebinar_v2_url}/ew-auto-login${state ? `?state=${state}` : ''}`;
                window.location.href = easyWebinarUrl;
            } catch (error) {
                console.error('Error navigating to EasyWebinar:', error);
                toast.error('An error occurred. Please try again later.');
                setIsLaunchingEasyWebinar(false);
            }
        }
    };

    const handleUpdatePayment = async () => {
        try {
            await ChargebeeService.openPaymentSection(async () => {
                // Refresh payment status after payment method is updated
                const metadata = decodeMetadataToken();
                const customerId = metadata?.companies?.[0]?.id;

                if (customerId) {
                    const response = await ChargebeeService.getPaymentMethodStatus(customerId);
                    setPaymentStatus(response.data);

                    const hasPaymentIssues = response.data.hasExpiredCard ||
                        response.data.hasInvalidCard ||
                        response.data.hasExpiringCard ||
                        !response.data.hasPaymentMethod;

                    setShowPaymentWarning(hasPaymentIssues);
                }
            });
        } catch (error) {
            console.error('Error opening payment section:', error);
            toast.error('Failed to open payment section. Please try again.');
        }
    };

    return (
        <>
            <Box sx={{ px: "0 !Important" }}>
                {showSubscriptionAlert && (
                    <SubscriptionAlert
                        variant="paused" // active, paused, cancelled
                        title="Your subscription has been paused"
                        description="Please renew your subscription to access EasyWebinar."
                        nextBillingDate="11 Oct, 2024"
                        actionLabel="Renew Subscription"
                        onAction={() => { /* handle action */ }}
                        onClose={() => setShowSubscriptionAlert(false)}
                    />
                )}

                {showPaymentWarning && paymentStatus && (
                    <PaymentWarning
                        paymentStatus={paymentStatus}
                        onUpdatePayment={handleUpdatePayment}
                        onDismiss={() => setShowPaymentWarning(false)}
                    />
                )}  
                <Box sx={{
                    mt: {
                        xs: '20px',
                    },
                }}>
                    {/* <Box 
                        sx={{ 
                            width: '100%', 
                            mt: 1, 
                            mb: 2, 
                            display: 'flex', 
                            justifyContent: 'center',
                            cursor: 'pointer',
                            transition: 'transform 0.2s ease-in-out',
                            '&:hover': {
                                transform: 'translateY(-2px)'
                            }
                        }}
                        onClick={() => window.open('https://easywebinar.com/easysuite/sales-crm/', '_blank')}
                    >
                        <img
                            src="https://easywebinar-site.easywebinar.com/images/easycrm-banner.png"
                            alt="Easycrm Banner"
                            fetchPriority="high"
                            style={{ width: '100%', maxWidth: 1600, borderRadius: 12, boxShadow: '0px 4px 24px 0px #2B2B3040' }}
                        />
                    </Box> */}

                    {/* sale banner */}
                    {shouldShowSale && (
                        <Box sx={{ width: '100%', mb: 3, display: 'flex', justifyContent: 'center' }}>  
                            <img
                                src="https://easywebinar-site.easywebinar.com/images/overview-sale-banner.png"
                                alt="Sale Banner"
                                style={{ 
                                    width: '100%', 
                                    maxWidth: 1600, 
                                    borderRadius: 12, 
                                    boxShadow: '0px 4px 24px 0px #2B2B3040',
                                    cursor: 'pointer'
                                }}
                                fetchPriority="high"
                                onClick={() => navigate('/subscription')}
                            />
                        </Box>
                    )}
                    {/* sale banner */}

                    {/* Sales banner with countdown */}
                    {/* <SalesBannerCountdown /> */}
                    
                    {/* promotional banner */}
                    {config.isPromontionBannerLive && (
                        <Box sx={{ width: '100%', mb: 3, display: 'flex', justifyContent: 'center' }}>  
                            <img
                                src="https://easywebinar-site.s3.amazonaws.com/images/event-promotion-sale-desktop-overiew-dasboard.png"
                                alt="Promotional Banner"
                                style={{ 
                                    width: '100%', 
                                    maxWidth: 1600, 
                                    borderRadius: 12, 
                                    boxShadow: '0px 4px 24px 0px #2B2B3040',
                                    cursor: 'pointer'
                                }}
                                fetchPriority="high"
                                onClick={() => window.open('https://get.easywebinar.com/webinar-secret', '_blank')}
                            />
                        </Box>
                    )}
                    {/* promotional banner */}
                    
                    <Grid container rowSpacing={{ xs: 3, sm: 3, md: 3 }} columnSpacing={{ xs: 3, sm: 2, md: 3 }}>
                        <Grid size={{ xs: 12, sm: 6, lg: 4, xl: 3 }}>
                            <Box
                                sx={{
                                    border: '1px solid #E8EAF0',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    flexDirection: 'column',
                                    position: 'relative',
                                    padding: {
                                        xs: '24px',
                                        md: '29px',
                                    },
                                    marginTop: {
                                        xs: '25px',
                                        sm: '0px',

                                    },
                                    borderRadius: '8px',
                                    backgroundColor: '#fff',
                                    width: '100%',
                                    boxShadow: '0px 4px 12px 0px #2B2B300F',
                                    height: '100%',

                                }}
                            >
                                <Box display="flex" alignItems="center" mb="30px" >
                                    <Avatar
                                        alt="Ryan Lee"
                                        src={userDetails.picture}
                                        sx={{
                                            height: '82px',
                                            width: '82px',
                                            marginRight: '12px',
                                            border: '3px solid #fff',
                                            backgroundColor: '#E0E0E0',
                                        }}
                                    />
                                    <Box>
                                        <Typography variant="h6" sx={{ fontWeight: 500, color: '#333', fontSize: '16px' }}>
                                            {userDetails.name || ''}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#666666', fontSize: '16px' }}>
                                            #{userDetails.username || ''}
                                        </Typography>
                                    </Box>
                                </Box>
                                <Box marginBottom='12px'>
                                    <Box sx={{ display: 'flex', gap: '20px', marginBottom: '8px' }}>
                                        <Tooltip title="Email" arrow><Box minWidth="24px" display="flex"><MsgIcon></MsgIcon></Box></Tooltip>
                                        <Typography color='#666666' fontSize='16px' sx={{ wordBreak: "break-all" }}>{userDetails.email || ''}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: '20px', marginBottom: '8px' }}>
                                        <Tooltip title="Company Name" arrow>
                                            <Box minWidth="24px" display="flex">
                                                <OfficeIcon></OfficeIcon>
                                            </Box>
                                        </Tooltip>
                                        <Typography color='#666666' fontSize='16px'>{userDetails.company || ''}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: '20px', marginBottom: '8px' }}>
                                        <Tooltip title="Phone Number" arrow>
                                            <Box minWidth="24px" display="flex">
                                                <PhoneIcon></PhoneIcon>
                                            </Box>
                                        </Tooltip>
                                        <Typography color='#666666' fontSize='16px'>{userDetails.phone || ''}</Typography>

                                    </Box>

                                </Box>
                                <Button
                                    variant='text'
                                    size='large'
                                    sx={{ textTransform: 'capitalize' }}
                                    onClick={() =>{ 
                                        MixpanelService.track('ua_overview_edit_details_clicked', { 'Click Edit Details on profile card': 'screen: "overview"' });
                                        navigate('/settings', { state: { activeTab: 0 } })
                                    }}
                                >
                                    Edit Details
                                </Button>
                            </Box>
                        </Grid>

                        {showSubscriptionCard && (
                            <Grid size={{ xs: 12, sm: 6, lg: 4, xl: 3 }} display='flex'>
                                <Box sx={{
                                    border: '1px solid #E8EAF0',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    flexDirection: 'column',
                                    position: 'relative',
                                    padding: {
                                        xs: '24px',
                                        md: '29px',
                                    },
                                    borderRadius: '8px',
                                    backgroundColor: '#fff',
                                    width: '100%',
                                    boxShadow: '0px 4px 12px 0px #2B2B300F',
                                }}>
                                    <Box display='flex' alignItems='center' flexDirection='column' marginBottom='36px' className='secuity-icon-wrapper'>
                                        <img src="https://easywebinar-site.easywebinar.com/images/autheticator-app-images/workspace-premium-outline-rounded.svg" alt="subscription icon" />
                                        <Typography color='#231F1F' fontWeight='500' fontSize='min(18px, calc(16px + 2 * ((100vw - 320px) / (1920 - 320))))' mb='12px' mt='30px' textAlign="center">Subscription</Typography>
                                        
                                        {/* Subscription Details */}
                                        {isLoadingSubscriptionDetails ? (
                                            <Box mt='12px' width='100%'>
                                                <Box display='flex' alignItems='center' mb='6px' gap='50px'>
                                                    <Typography color='#666666' fontSize='13px' sx={{ minWidth: '80px' }}>Plan:</Typography>
                                                    <SkeletonBox width='60px' />
                                                </Box>
                                                <Box display='flex' alignItems='center' mb='6px' gap='50px'>
                                                    <Typography color='#666666' fontSize='13px' sx={{ minWidth: '80px' }}>Next Billing:</Typography>
                                                    <SkeletonBox width='100px' />
                                                </Box>
                                                <Box display='flex' alignItems='center' gap='50px'>
                                                    <Typography color='#666666' fontSize='13px' sx={{ minWidth: '80px' }}>Status:</Typography>
                                                    <SkeletonBox width='50px' height='24px' />
                                                </Box>
                                            </Box>
                                        ) : subscriptionDetails && subscriptionDetails.length > 0 ? (
                                            <Box mt='12px' width='100%'>
                                                {(() => {
                                                    const subscription = subscriptionDetails[0];
                                                    const planName = subscription.subscription_items?.[0]?.itemName;
                                                    const renewalDate = formatDate(subscription.subscription.nextBillingAt);
                                                    const statusInfo = getStatusInfo(subscription.subscription.status);
                                                    
                                                    return (
                                                        <>
                                                            <Box display='flex' alignItems='center' mb='6px' gap='50px'>
                                                                <Typography color='#666666' fontSize='13px' sx={{ minWidth: '80px' }}>Plan:</Typography>
                                                                <Typography color='#231F1F' fontSize='13px' fontWeight='500'>{planName}</Typography>
                                                            </Box>
                                                            <Box display='flex' alignItems='center' mb='6px' gap='50px'>
                                                                <Typography color='#666666' fontSize='13px' sx={{ minWidth: '80px' }}>Next Billing:</Typography>
                                                                <Typography color='#231F1F' fontSize='13px' fontWeight='500'>{renewalDate}</Typography>
                                                            </Box>
                                                            <Box display='flex' alignItems='center' gap='50px'>
                                                                <Typography color='#666666' fontSize='13px' sx={{ minWidth: '80px' }}>Status:</Typography>
                                                                <Box 
                                                                    sx={{ 
                                                                        backgroundColor: `${statusInfo.color}20`,
                                                                        color: statusInfo.color,
                                                                        border: `1px solid ${statusInfo.color}`,
                                                                        padding: '4px 12px',
                                                                        borderRadius: '6px',
                                                                        fontSize: '12px',
                                                                        fontWeight: '500'
                                                                    }}
                                                                >
                                                                    {statusInfo.text}
                                                                </Box>
                                                            </Box>
                                                        </>
                                                    );
                                                })()}
                                            </Box>
                                        ) : (
                                            <Typography color='#666666' fontSize='13px' mt='12px' textAlign='center'>
                                                No subscription details available
                                            </Typography>
                                        )}
                                    </Box>
                                    <Button variant='text' size='large' sx={{ textTransform: 'capitalize' }} onClick={() => {
                                        MixpanelService.track('ua_overview_manage_subscription_clicked', {
                                            'Click "Manage Subscription"':'screen: "overview"',
                                            'current_plan': subscriptionDetails?.[0]?.subscription_items?.[0]?.itemName || '' });
                                        navigate('/subscription')
                                    }}>Manage Subscription</Button>
                                </Box>
                            </Grid>
                        )}

                        {showWorkspacesCard && (
                            <Grid size={{ xs: 12, sm: 6, lg: 4, xl: 3 }} display='flex'>
                                <Box sx={{
                                    border: '1px solid #E8EAF0',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    flexDirection: 'column',
                                    position: 'relative',
                                    padding: {
                                        xs: '24px',
                                        md: '29px',
                                    },
                                    borderRadius: '8px',
                                    backgroundColor: '#fff',
                                    width: '100%',
                                    boxShadow: '0px 4px 12px 0px #2B2B300F',
                                }}>
                                    <Box display='flex' alignItems='center' flexDirection='column' marginBottom='36px' className='secuity-icon-wrapper'>
                                        <img src="https://easywebinar-site.easywebinar.com/images/autheticator-app-images/workspaces-outline.svg" alt="Workspaces icon" />
                                        <Typography color='#231F1F' fontWeight='500' fontSize='min(18px, calc(16px + 2 * ((100vw - 320px) / (1920 - 320))))' mb='12px' mt='30px' textAlign="center">Workspaces</Typography>
                                        <Typography color='#666666' fontSize='min(16px, calc(14px + 2 * ((100vw - 320px) / (1920 - 320))))' textAlign='center'>Manage multiple accounts for your workspace</Typography>
                                    </Box>
                                    <Button variant='text' size='large' sx={{ textTransform: 'capitalize' }} onClick={() =>{ 
                                        MixpanelService.track('ua_overview_manage_workspaces_clicked', { 'Click "Manage Workspaces"': 'screen: "overview"' });
                                        navigate('/workspaces')
                                    }}>Manage Workspaces</Button>
                                </Box>
                            </Grid>
                        )}

                        <Grid size={{ xs: 12, sm: 6, lg: 4, xl: 3 }} display='flex'>
                            <Box sx={{
                                border: '1px solid #E8EAF0',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                flexDirection: 'column',
                                position: 'relative',
                                padding: {
                                    xs: '24px',
                                    md: '29px',
                                },
                                borderRadius: '8px',
                                backgroundColor: '#fff',
                                width: '100%',
                                boxShadow: '0px 4px 12px 0px #2B2B300F',
                            }}>
                                <Box display='flex' alignItems='center' flexDirection='column' marginBottom='36px' className='secuity-icon-wrapper'>
                                    <img src="https://easywebinar-site.easywebinar.com/images/autheticator-app-images/icons/light_campaign-outline.svg" alt="social media icon" />
                                    <Typography color='#231F1F' fontWeight='500' fontSize='min(18px, calc(16px + 2 * ((100vw - 320px) / (1920 - 320))))' mb='12px' mt='30px' textAlign="center">Social Media</Typography>
                                    <Typography color='#666666' fontSize='min(16px, calc(14px + 2 * ((100vw - 320px) / (1920 - 320))))' textAlign='center'>Stay connected for updates, tips, and more</Typography>
                                </Box>
                                <Box display="flex" alignItems="center" justifyContent="center" gap="20px" marginTop="20px">
                                    <a
                                        href="https://www.linkedin.com/company/easywebinar/"
                                        target="_blank"
                                        style={{ display: "flex" }}
                                        onClick={() => {
                                            MixpanelService.track('ua_overview_social_link_clicked', { platform: 'linkedin' });
                                        }}
                                    >
                                        <Tooltip title="LinkedIn" arrow>
                                            <img src="https://easywebinar-site.easywebinar.com/images/autheticator-app-images/linkedin.png" alt="linkedin" />
                                        </Tooltip>
                                    </a>
                                    <a
                                        href="https://www.facebook.com/easywebinar"
                                        target="_blank"
                                        style={{ display: "flex" }}
                                        onClick={() => {
                                            MixpanelService.track('ua_overview_social_link_clicked', { platform: 'facebook' });
                                        }}
                                    >
                                        <Tooltip title="Facebook" arrow>
                                            <img src="https://easywebinar-site.easywebinar.com/images/autheticator-app-images/fb-icon.png" alt="facebook" />
                                        </Tooltip>
                                    </a>
                                    <a
                                        href="https://www.youtube.com/@EasyWebinar"
                                        target="_blank"
                                        style={{ display: "flex" }}
                                        onClick={() => {
                                            MixpanelService.track('ua_overview_social_link_clicked', { platform: 'youtube' });
                                        }}
                                    >
                                        <Tooltip title="YouTube" arrow>
                                            <img src="https://easywebinar-site.easywebinar.com/images/autheticator-app-images/youtube-icon.png" alt="youtube" />
                                        </Tooltip>
                                    </a>
                                    <a
                                        href="https://x.com/easywebinar"
                                        target="_blank"
                                        style={{ display: "flex" }}
                                        onClick={() => {
                                            MixpanelService.track('ua_overview_social_link_clicked', { platform: 'x' });
                                        }}
                                    >
                                        <Tooltip title="Twitter" arrow>
                                            <img src="https://easywebinar-site.easywebinar.com/images/autheticator-app-images/x-icon.png" alt="twitter" />
                                        </Tooltip>
                                    </a>
                                </Box>

                            </Box>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, lg: 4, xl: 3 }} display='flex'>
                            <Box sx={{
                                border: '1px solid #E8EAF0',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                flexDirection: 'column',
                                position: 'relative',
                                padding: {
                                    xs: '24px',
                                    md: '29px',
                                },
                                borderRadius: '8px',
                                backgroundColor: '#fff',
                                width: '100%',
                                boxShadow: '0px 4px 12px 0px #2B2B300F',
                            }}>
                                <Box display='flex' alignItems='center' flexDirection='column' marginBottom='36px' className='secuity-icon-wrapper'>
                                    <img src="https://easywebinar-site.easywebinar.com/images/autheticator-app-images/hub-outline.svg" alt="Acedemy icon" />
                                    <Typography color='#231F1F' fontWeight='500' fontSize='min(18px, calc(16px + 2 * ((100vw - 320px) / (1920 - 320))))' mb='12px' mt='30px' textAlign="center">EasyWebinar Academy</Typography>
                                    <Typography color='#666666' fontSize='min(16px, calc(14px + 2 * ((100vw - 320px) / (1920 - 320))))' textAlign='center'>Learn everything about Easywebinar</Typography>
                                </Box>
                                <Button
                                    variant='text'
                                    size='large'
                                    sx={{ textTransform: 'capitalize' }}
                                    href='https://resources.easywebinar.com/'
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={() => {
                                        MixpanelService.track('ua_overview_academy_clicked', { 'Click "Explore Now" (EasyWebinar Academy)': 'screen: "overview"' });
                                    }}
                                >
                                    Explore Now
                                </Button>

                            </Box>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, lg: 4, xl: 3 }} display='flex'>
                            <Box sx={{
                                border: '1px solid #E8EAF0',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                flexDirection: 'column',
                                position: 'relative',
                                padding: {
                                    xs: '24px',
                                    md: '29px',
                                },
                                borderRadius: '8px',
                                backgroundColor: '#fff',
                                width: '100%',
                                boxShadow: '0px 4px 12px 0px #2B2B300F',
                            }}>
                                <Box display='flex' alignItems='center' flexDirection='column' marginBottom='36px' className='secuity-icon-wrapper'>
                                    <img src="https://easywebinar-site.easywebinar.com/images/autheticator-app-images/menu-book-outline.svg" alt="Help center icon" />
                                    <Typography color='#231F1F' fontWeight='500' fontSize='min(18px, calc(16px + 2 * ((100vw - 320px) / (1920 - 320))))' mb='12px' mt='30px' textAlign="center">Help Center</Typography>
                                    <Typography color='#666666' fontSize='min(16px, calc(14px + 2 * ((100vw - 320px) / (1920 - 320))))' textAlign='center'>Get Advice and answers to your queries from EasyWebinar Team</Typography>
                                </Box>
                                <Button variant='text' size='large' sx={{ textTransform: 'capitalize' }} href='https://support.easywebinar.com/en' target='_blank' rel="noopener noreferrer" onClick={() => {
                                    MixpanelService.track('ua_overview_help_center_clicked', { 'Click "Knowledge Base"': 'screen: "overview"' });
                                }}>Knowledge Base</Button>

                            </Box>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, lg: 4, xl: 3 }} display='flex'>
                            <Box sx={{
                                border: '1px solid #E8EAF0',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                flexDirection: 'column',
                                position: 'relative',
                                padding: {
                                    xs: '24px',
                                    md: '29px',
                                },
                                borderRadius: '8px',
                                backgroundColor: '#fff',
                                width: '100%',
                                boxShadow: '0px 4px 12px 0px #2B2B300F',
                            }}>
                                <Box display='flex' alignItems='center' flexDirection='column' marginBottom='36px' className='secuity-icon-wrapper'>
                                    <img src="https://easywebinar-site.easywebinar.com/images/autheticator-app-images/hugeicons_internet.svg" alt="Connect icon" />
                                    <Typography color='#231F1F' fontWeight='500' fontSize='min(18px, calc(16px + 2 * ((100vw - 320px) / (1920 - 320))))' mb='12px' mt='30px' textAlign="center">EasyWebinar Community</Typography>
                                    <Typography color='#666666' fontSize='min(16px, calc(14px + 2 * ((100vw - 320px) / (1920 - 320))))' textAlign='center'>Join our community to stay updated, learn and grow</Typography>
                                </Box>
                                <Button variant='text' size="large" sx={{ textTransform: 'capitalize' }} onClick={() => 
                                {
                                    MixpanelService.track('ua_overview_community_clicked', { 'Click "Explore Community"': 'screen: "overview"' });
                                    window.open('https://www.facebook.com/share/g/1Ag9KaneVS/', '_blank')
                                }}>Explore Community</Button>

                            </Box>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, lg: 4, xl: 3 }} display='flex'>
                            <Box sx={{
                                border: '1px solid #E8EAF0',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                flexDirection: 'column',
                                position: 'relative',
                                padding: {
                                    xs: '24px',
                                    md: '29px',
                                },
                                borderRadius: '8px',
                                backgroundColor: '#fff',
                                width: '100%',
                                boxShadow: '0px 4px 12px 0px #2B2B300F',
                            }}>
                                <Box display='flex' alignItems='center' flexDirection='column' marginBottom='36px' className='secuity-icon-wrapper'>
                                    <img src="https://easywebinar-site.easywebinar.com/images/autheticator-app-images/ion_apps-outline.svg" alt="our apps" />
                                    <Typography color='#231F1F' fontWeight='500' fontSize='min(18px, calc(16px + 2 * ((100vw - 320px) / (1920 - 320))))' mb='12px' mt='30px' textAlign="center">Our Apps</Typography>
                                    <Typography color='#666666' fontSize='min(16px, calc(14px + 2 * ((100vw - 320px) / (1920 - 320))))' textAlign='center'>Switch seamlessly between our application</Typography>
                                </Box>
                                <Box display="flex" alignItems="center" justifyContent="center" gap="20px" marginTop="13px">
                                    <a
                                        href={`https://test-v2.easywebinar.com/ew-auto-login${localStorage.getItem(STORAGE_KEYS.STATE) ? `?state=${localStorage.getItem(STORAGE_KEYS.STATE)}` : ''}`}
                                        onClick={(e) => {
                                            MixpanelService.track('ua_overview_app_switcher_clicked', { 'app_name': 'EasyWebinar' });
                                            handleEasyWebinarClick(e);
                                        }}
                                        style={{
                                            display: "flex",
                                            cursor: isLaunchingEasyWebinar ? "wait" : "pointer",
                                            opacity: 1
                                        }}
                                    >
                                        <Tooltip
                                            title={isLaunchingEasyWebinar ? "Loading..." : "Easywebinar"}
                                            arrow
                                            slotProps={{
                                                tooltip: {
                                                    sx: {
                                                        backgroundColor: '#000',
                                                        color: '#fff',
                                                        '& .MuiTooltip-arrow': {
                                                            color: '#000',
                                                        },
                                                        height: '36px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        fontSize: '14px',
                                                        padding: '0 16px',
                                                    }
                                                },
                                            }}
                                        >
                                            {isLaunchingEasyWebinar ? (
                                                <Loader sx={{position: 'fixed', zIndex: 999999, top: 0, right: 0, left: 0, bottom: 0}} fullHeight minHeight='100vh' text='Redirecting to EasyWebinar...' fontSize='16px'/>
                                            ) : (
                                                <img src="https://easywebinar-site.easywebinar.com/images/autheticator-app-images/ew-logo-md.png" alt="easywebinar" />
                                            )}
                                        </Tooltip>
                                    </a>
                                    <a
                                        href={isEasyCRMEnabled ? `${config.easycrm_url}/user/MaintainSession${localStorage.getItem(STORAGE_KEYS.STATE) ? `?state=${localStorage.getItem(STORAGE_KEYS.STATE)}` : ''}` : undefined}
                                        onClick={isEasyCRMEnabled ? (e) => {
                                            MixpanelService.track('ua_overview_app_switcher_clicked', { 'app_name': 'EasyCRM' });
                                            handleEasyCRMClick(e);
                                        } : (e) => {
                                            e.preventDefault();
                                            const crmEntitlement = checkFeatureEntitlement('easycrm');
                                            toast.error(crmEntitlement.message || 'Upgrade plan to access EasyCRM');
                                        }}
                                        style={{ 
                                            display: "flex", 
                                            cursor: isEasyCRMEnabled ? "pointer" : "not-allowed", 
                                            opacity: isEasyCRMEnabled ? "1" : "0.7" 
                                        }}
                                    >
                                        <Tooltip
                                            title={isEasyCRMEnabled ? "EasyCRM" : "Upgrade plan to access EasyCRM"}
                                            arrow
                                            slotProps={{
                                                tooltip: {
                                                    sx: {
                                                        backgroundColor: '#000',
                                                        color: '#fff',
                                                        '& .MuiTooltip-arrow': {
                                                            color: '#000',
                                                        },
                                                        height: '36px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        fontSize: '14px',
                                                        padding: '0 16px',
                                                    }
                                                },
                                            }}
                                        ><img src="https://easywebinar-site.easywebinar.com/images/autheticator-app-images/Easycrm-logo-md.png" alt="easycrm" />
                                        </Tooltip>
                                    </a>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>

                {/* floating icons */}
                <Box
                    sx={{
                        position: 'fixed',
                        bottom: '50%',
                        right: '0',
                        zIndex: 1000,
                        transform: "translateY(50%)",
                        opacity: "0.7",
                        pointerEvents: "none",
                        "&:hover": {
                            opacity: "1"
                        }
                    }}
                    className="floating-icons"
                >
                    <Box
                        borderRadius="8px"
                        padding="11px 12px"
                        border="1px solid #E8EAF0"
                        bgcolor="#FFFFFF"
                        boxShadow="0px 4px 12px 0px rgba(43, 43, 48, 0.06)"
                        display="flex"
                        alignItems="center"
                        gap="40px"
                        height={{ xs: "60px", xl: "70px" }}
                        sx={{
                            cursor: 'pointer',
                            transform: { xs: "translateX(79%)", xl: "translateX(76%)" },
                            transition: "all .5s ease",
                            pointerEvents: "auto",
                            "&:hover": {
                                transform: "translateX(0)"
                            }
                        }}

                    >
                        <img className='review-icon' src="https://easywebinar-site.easywebinar.com/images/autheticator-app-images/captera-icon.png" alt="captera" />
                        <Box onClick={() =>{
                             MixpanelService.track('ua_overview_review_platform_clicked', { platform: '"capterra"' });
                             window.open('https://www.capterra.in/software/163102/easywebinar', '_blank')}} mr="39px" className="captera-review">
                            <img style={{ display: "flex" }} src="https://easywebinar-site.easywebinar.com/images/autheticator-app-images/5-Stars.png" alt="5-Stars" />
                            <Typography variant="body1" color='#6A6A6A' fontWeight="200" textAlign="center" fontSize='min(16px, calc(14px + 2 * ((100vw - 320px) / (1920 - 320))))'
                            >Review us on</Typography>
                        </Box>
                    </Box>
                    <Box
                        borderRadius="8px"
                        padding="11px 12px"
                        border="1px solid #E8EAF0"
                        bgcolor="#FFFFFF"
                        boxShadow="0px 4px 12px 0px rgba(43, 43, 48, 0.06)"
                        display="flex"
                        alignItems="center"
                        gap="40px"
                        height={{ xs: "60px", xl: "70px" }}
                        sx={{
                            cursor: 'pointer',
                            transform: { xs: "translateX(79%)", xl: "translateX(76%)" },
                            transition: "all .5s ease",
                            pointerEvents: "auto",
                            "&:hover": {
                                transform: "translateX(0)"
                            }
                        }}
                    >
                        <img className='review-icon' src="https://easywebinar-site.easywebinar.com/images/autheticator-app-images/g2-seeklogo.png" alt="g2" />
                        <Box onClick={() =>{
                             MixpanelService.track('ua_overview_review_platform_clicked', { platform: '"g2"' });
                             window.open('https://www.g2.com/products/easywebinar/reviews', '_blank')}} mr="39px" className="captera-review">
                            <img style={{ display: "flex" }} src="https://easywebinar-site.easywebinar.com/images/autheticator-app-images/g2-review-5stars.png" alt="5-Stars" />
                            <Typography
                                variant="body1"
                                color='#6A6A6A'
                                fontWeight="200"
                                textAlign="center"
                                fontSize='min(16px, calc(14px + 2 * ((100vw - 320px) / (1920 - 320))))'
                            >
                                Review us on
                            </Typography>
                        </Box>
                    </Box>
                </Box>
                {/* floating icons */}
            </Box >
        </>
    )
}

export default Overview
